<script lang="ts">
  import {createEventDispatcher} from 'svelte';
  import InlineSVG from 'svelte-inline-svg';

  import handleClickOutside from '../../utils/handle-click-outside';
  import a11yClick from '../../utils/a11y-click';

  import chevronDownIcon from '../../assets/icons/chevron-down.svg';

  export let options: Array<{value: string; label: string}>;
  export let value: string | Array<string> | null;
  export let name: string;
  export let multiple = false;
  export let disabled = false;

  let open = false;

  const dispatch = createEventDispatcher();

  $: selectedOptions = options.filter(option =>
    Array.isArray(value) ? value.includes(option.value) : option.value === value
  );

  function handleChange(option: {value: string; label: string}) {
    if (multiple) {
      const newValues = (value as Array<string>).includes(option.value)
        ? (value as Array<string>).filter(element => element !== option.value)
        : [...(value as Array<string>), option.value];
      value = newValues.filter(Boolean);
    } else {
      value = option.value && value === option.value ? null : option.value;
      open = false;
    }

    dispatch('change', value);
  }
</script>

<style lang="postcss">
  .dropdown {
    position: relative;
    display: inline-block;
    width: var(--ubilabs-dropdown-width);
    max-width: 100%;
    font-size: var(--ubilabs-dropdown-font-size);
  }

  .dropdownHeader {
    display: inline-flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    height: var(--ubilabs-dropdown-height);
    padding: var(--ubilabs-dropdown-padding);
    background-color: var(--ubilabs-dropdown-background-color);
    border: var(--ubilabs-input-border);
    border-radius: var(--ubilabs-dropdown-border-radius);
    outline: none;
    overflow: hidden;
    font-size: var(--ubilabs-dropdown-font-size);
    font-family: var(--ubilabs-font-family);
    color: var(--ubilabs-dropdown-color);
    cursor: default;
  }

  .dropdownHeader:hover:not(:disabled):not(.readOnly),
  .dropdownHeader:focus-visible:not(:disabled):not(.readOnly),
  .dropdownHeader:active:not(:disabled):not(.readOnly) {
    background-color: var(--ubilabs-dropdown-option-hover-background-color);
  }

  .dropdownHeader:focus-visible:not(:disabled):not(.readOnly) {
    box-shadow: var(--ubilabs-focus-box-shadow);
  }

  .dropdownHeader.readOnly:not(:disabled) {
    background-color: var(--ubilabs-dropdown-readonly-background-color);
    color: var(--ubilabs-dropdown-readonly-color);
  }

  .dropdownHeader:disabled {
    background-color: var(--ubilabs-dropdown-disabled-background-color);
    color: var(--ubilabs-dropdown-disabled-color);
  }

  .dropdownHeader.isOpen {
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;
  }

  .label {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

  .label.noSelection {
    color: var(--ubilabs-gray-dark-2);
  }

  .dropdownHeader .icon {
    margin: var(--ubilabs-dropdown-icon-margin);
    color: var(--ubilabs-dropdown-icon-color);
    font-size: var(--ubilabs-dropdown-icon-size);
    pointer-events: none;
  }

  .dropdownOptions {
    z-index: 1;
    position: absolute;
    top: 100%;
    left: 0;
    display: inline-block;
    width: var(--ubilabs-dropdown-width);
    max-width: 100%;
    background-color: var(--ubilabs-dropdown-background-color);
    border: var(--ubilabs-dropdown-options-border);
    border-top: none;
    border-bottom-right-radius: var(--ubilabs-dropdown-border-radius);
    border-bottom-left-radius: var(--ubilabs-dropdown-border-radius);
    font-size: var(--ubilabs-dropdown-font-size);
  }

  .dropdownOptions > ul {
    max-height: var(--ubilabs-dropdown-options-height);
    margin: 0;
    padding: var(--ubilabs-dropdown-options-padding);
    list-style: none;
    overflow: auto;
    background: /* Shadow cover top */ linear-gradient(
          rgb(var(--scroll-container-background-color-rgb)) 30%,
          rgba(var(--scroll-container-background-color-rgb), 0)
        )
        center top,
      /* Shadow cover right */
        linear-gradient(
          to right,
          rgba(var(--scroll-container-background-color-rgb), 0),
          rgb(var(--scroll-container-background-color-rgb)) 70%
        )
        center right,
      /* Shadow cover bottom */
        linear-gradient(
          rgba(var(--scroll-container-background-color-rgb), 0),
          rgb(var(--scroll-container-background-color-rgb)) 70%
        )
        center bottom,
      /* Shadow cover left */
        linear-gradient(
          to right,
          rgb(var(--scroll-container-background-color-rgb)) 30%,
          rgba(var(--scroll-container-background-color-rgb), 0)
        )
        center left,
      /* Shadow top */
        radial-gradient(
          farthest-side at 50% 0,
          rgba(var(--scroll-container-shadow-color-rgb), 0.3),
          rgba(var(--scroll-container-shadow-color-rgb), 0)
        )
        center top,
      /* Shadow right */
        radial-gradient(
          farthest-side at 100% 50%,
          rgba(var(--scroll-container-shadow-color-rgb), 0.3),
          rgba(var(--scroll-container-shadow-color-rgb), 0)
        )
        center right,
      /* Shadow bottom */
        radial-gradient(
          farthest-side at 50% 100%,
          rgba(var(--scroll-container-shadow-color-rgb), 0.3),
          rgba(var(--scroll-container-shadow-color-rgb), 0)
        )
        center bottom,
      /* Shadow left */
        radial-gradient(
          farthest-side at 0 50%,
          rgba(var(--scroll-container-shadow-color-rgb), 0.3),
          rgba(var(--scroll-container-shadow-color-rgb), 0)
        )
        center left;
    background-repeat: no-repeat;
    background-size: 100% calc(var(--scroll-container-shadow-size) * 2),
      calc(var(--scroll-container-shadow-size) * 2) 100%,
      100% calc(var(--scroll-container-shadow-size) * 2),
      calc(var(--scroll-container-shadow-size) * 2) 100%,
      100% var(--scroll-container-shadow-size),
      var(--scroll-container-shadow-size) 100%,
      100% var(--scroll-container-shadow-size),
      var(--scroll-container-shadow-size) 100%;
    background-attachment: local, local, local, local, scroll, scroll, scroll,
      scroll;
  }

  .dropdownOptions .option {
    padding: var(--ubilabs-dropdown-option-padding);
    outline: none;
    cursor: default;
  }

  .dropdownOptions .option:hover,
  .dropdownOptions .option:focus-visible {
    background-color: var(--ubilabs-dropdown-option-hover-background-color);
  }

  .dropdownOptions .option.isSelected {
    background-color: var(--ubilabs-dropdown-option-selected-background-color);
  }

  .dropdownOptions .option.isSelected:hover,
  .dropdownOptions .option.isSelected:focus-visible {
    background-color: var(
      --ubilabs-dropdown-option-selected-hover-background-color
    );
  }
</style>

<div
  class="dropdown"
  use:handleClickOutside
  on:click_outside={() => (open = false)}>
  <button
    {...$$restProps}
    class="dropdownHeader {open ? 'isOpen' : ''} {$$props.class}"
    on:click={() => (open = !open)}
    aria-controls={`${name}-options`}
    aria-haspopup={true}
    aria-expanded={open}
    disabled={!options.length || disabled}>
    <span class="label {!selectedOptions.length ? 'noSelection' : ''}">
      {selectedOptions.length
        ? selectedOptions.map(selectedOption => selectedOption.label).join(', ')
        : `Select ${multiple ? 'entries' : 'entry'}`}
    </span>
    <span class="icon">
      <InlineSVG src={chevronDownIcon} />
    </span>
  </button>

  {#if open}
    <div class="dropdownOptions">
      <ul>
        {#each options as option}
          <li
            class="option {selectedOptions.some(
              selectedOption => selectedOption.value === option.value
            )
              ? 'isSelected'
              : ''}"
            on:click={() => handleChange(option)}
            on:keypress={event => {
              if (a11yClick(event)) {
                handleChange(option);
              }
            }}
            tabIndex={0}>
            {option.label}
          </li>
        {/each}
      </ul>
    </div>
  {/if}
</div>
